import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Package, Airplane, ChartBar, Globe, ShieldCheck,
  ArrowRight, Check, Star, Users, Barcode, FileText,
  EnvelopeSimple, WhatsappLogo, CreditCard, X, MagnifyingGlass,
  MapPin, Phone, Lightning, AirplaneTilt, Cube, Path, BellRinging
} from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import AnimatedDemo from '../components/AnimatedDemo';
import { ChatWidget } from '../components/ChatWidget';
import SEOHead from '../components/SEOHead';
import PublicNav from '../components/PublicNav';
import PublicFooter from '../components/PublicFooter';
import { usePublicT } from '../i18n/usePublicT';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* ─── Stagger Text Component: animates words in with GSAP ─── */
const StaggerText = ({ text, className = '', as: Tag = 'span', trigger, delay = 0, wordClassName = '' }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    const words = ref.current.querySelectorAll('.stagger-word');
    if (!words.length) return;
    gsap.fromTo(words,
      { y: 40, opacity: 0, rotateX: -40 },
      {
        y: 0, opacity: 1, rotateX: 0,
        stagger: 0.06, duration: 0.6, delay,
        ease: 'power3.out',
        scrollTrigger: trigger ? { trigger: trigger.current || ref.current, start: 'top 85%', toggleActions: 'play none none none' } : undefined,
      }
    );
  }, [text, trigger, delay]);

  const words = (text || '').split(' ');
  return (
    <Tag ref={ref} className={className} style={{ perspective: '600px' }}>
      {words.map((word, i) => (
        <span key={i} className={`stagger-word inline-block ${wordClassName}`} style={{ opacity: 0 }}>
          {word}{i < words.length - 1 ? '\u00A0' : ''}
        </span>
      ))}
    </Tag>
  );
};

/* ─── Cargo Plane SVG — Clean side-view silhouette ─── */
const PlaneSVG = ({ className = '' }) => (
  <img src="/assets/cargo-plane.png" alt="Cargo Plane" className={className} style={{ width: '250px', height: 'auto', opacity: 1 }} />
);

export default function LandingPage() {
  const navigate = useNavigate();
  const pt = usePublicT();
  const [showSignup, setShowSignup] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('starter');
  const [signupData, setSignupData] = useState({ company_name: '', email: '', password: '', phone: '' });
  const [contactData, setContactData] = useState({ name: '', email: '', company: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [heroTrackingNumber, setHeroTrackingNumber] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('stripe');

  // GSAP refs
  const mainRef = useRef(null);
  const planeRef = useRef(null);
  const heroTextRef = useRef(null);
  const heroBadgeRef = useRef(null);
  const heroCtaRef = useRef(null);
  const heroTrackRef = useRef(null);
  const featTitleRef = useRef(null);
  const featGridRef = useRef(null);
  const howTitleRef = useRef(null);
  const howStepsRef = useRef(null);
  const demoRef = useRef(null);
  const portalRef = useRef(null);
  const pricingRef = useRef(null);
  const ctaRef = useRef(null);
  const carriersRef = useRef(null);

  const tPLANS = [
    { id: 'starter', name: pt('price.starter'), price: 99, description: pt('price.starterDesc'),
      features: [pt('planFeat.under100'), pt('planFeat.3team'), pt('planFeat.packageTracking'), pt('planFeat.barcode'), pt('planFeat.csvImport'), pt('planFeat.batchStatus'), pt('planFeat.basicReports'), pt('planFeat.clientPortal'), pt('planFeat.trilingual')],
      cta: pt('price.getStarted'), popular: false },
    { id: 'pro', name: pt('price.professional'), price: 249, description: pt('price.professionalDesc'),
      features: [pt('planFeat.over100'), pt('planFeat.10team'), pt('planFeat.everythingStarter'), pt('planFeat.advancedReports'), pt('planFeat.excelPdf'), pt('planFeat.emailNotif'), pt('planFeat.invoiceGen'), pt('planFeat.stripePaypal'), pt('planFeat.selfService'), pt('planFeat.whatsapp'), pt('planFeat.pushNotif')],
      cta: pt('price.startPro'), popular: true },
    { id: 'enterprise', name: pt('price.enterprise'), price: null, description: pt('price.enterpriseDesc'),
      features: [pt('planFeat.unlimited'), pt('planFeat.unlimitedUsers'), pt('planFeat.everythingPro'), pt('planFeat.anyLang'), pt('planFeat.customBranding'), pt('planFeat.prioritySupport'), pt('planFeat.apiAccess'), pt('planFeat.dedicatedMgr'), pt('planFeat.customInteg'), pt('planFeat.sla')],
      cta: pt('price.contactUs'), popular: false },
  ];

  const tFEATURES = [
    { icon: Airplane, title: pt('feat.packageTracking'), desc: pt('feat.packageTrackingDesc'), color: '#0080FF' },
    { icon: Barcode, title: pt('feat.barcodeScanner'), desc: pt('feat.barcodeScannerDesc'), color: '#FF9900' },
    { icon: Users, title: pt('feat.customerPortal'), desc: pt('feat.customerPortalDesc'), color: '#0080FF' },
    { icon: FileText, title: pt('feat.invoicingPdf'), desc: pt('feat.invoicingPdfDesc'), color: '#FF9900' },
    { icon: CreditCard, title: pt('feat.stripePaypal'), desc: pt('feat.stripePaypalDesc'), color: '#0080FF' },
    { icon: ChartBar, title: pt('feat.reportsAnalytics'), desc: pt('feat.reportsAnalyticsDesc'), color: '#FF9900' },
    { icon: Globe, title: pt('feat.trilingual'), desc: pt('feat.trilingualDesc'), color: '#0080FF' },
    { icon: EnvelopeSimple, title: pt('feat.emailNotifications'), desc: pt('feat.emailNotificationsDesc'), color: '#FF9900' },
    { icon: WhatsappLogo, title: pt('feat.whatsapp'), desc: pt('feat.whatsappDesc'), color: '#25D366' },
    { icon: Lightning, title: pt('feat.pushNotifications'), desc: pt('feat.pushNotificationsDesc'), color: '#F43F5E' },
  ];

  /* ─── GSAP Animations ─── */
  useEffect(() => {
    // Small delay to ensure DOM is fully rendered
    const timer = setTimeout(() => {
      const ctx = gsap.context(() => {
        // ── Plane: visible on landing (right side of screen), faces LEFT (scaleX:-1)
        //    because the source PNG points right by default. Then flies right → left.
        gsap.set(planeRef.current, { x: '70vw', y: '15vh', rotation: 0, scaleX: -1, scaleY: 1, opacity: 1 });

        // Gentle idle bobbing while user is looking at landing screen
        gsap.to(planeRef.current, {
          y: '+=20',
          duration: 2.4,
          ease: 'sine.inOut',
          yoyo: true,
          repeat: -1,
        });

        // Scroll-driven flight: right edge → off-screen left, nose-first
        gsap.to(planeRef.current, {
          x: '-30vw', ease: 'none',
          scrollTrigger: { trigger: mainRef.current, start: 'top top', end: '30% top', scrub: 1.5 },
        });

        // Second fly-by at mid page — LEFT → RIGHT (nose faces right, so no flip)
        ScrollTrigger.create({
          trigger: mainRef.current,
          start: '55% top',
          end: '80% top',
          scrub: 1.5,
          animation: gsap.fromTo(planeRef.current,
            { x: '-25vw', y: '60vh', scaleX: 1, scaleY: 1, opacity: 0, immediateRender: false },
            { x: '110vw', y: '25vh', scaleX: 1, scaleY: 1, opacity: 1, ease: 'none', immediateRender: false }
          ),
        });

        // ── Hero Section (badge + CTA only — text handled by StaggerText) ──
        const heroTl = gsap.timeline({ defaults: { ease: 'power3.out' } });
        heroTl
          .from(heroBadgeRef.current, { y: -30, opacity: 0, scale: 0.8, duration: 0.6 })
          .from(heroCtaRef.current, { y: 30, opacity: 0, duration: 0.6 }, '+=0.6')
          .from(heroTrackRef.current, { y: 40, opacity: 0, duration: 0.6 }, '-=0.3');

        // ── Features Grid (titles handled by StaggerText) ──
        if (featGridRef.current) {
          gsap.fromTo(featGridRef.current.children,
            { y: 60, opacity: 0, scale: 0.9 },
            { y: 0, opacity: 1, scale: 1, stagger: 0.08, duration: 0.6, ease: 'back.out(1.4)',
              scrollTrigger: { trigger: featGridRef.current, start: 'top 90%', toggleActions: 'play none none none' } }
          );
        }

        // ── How It Works Steps (title handled by StaggerText) ──
        if (howStepsRef.current) {
          gsap.fromTo(howStepsRef.current.children,
            { x: -80, opacity: 0 },
            { x: 0, opacity: 1, stagger: 0.15, duration: 0.8, ease: 'power3.out',
              scrollTrigger: { trigger: howStepsRef.current, start: 'top 85%', toggleActions: 'play none none none' } }
          );
        }

        // ── Demo Section ──
        if (demoRef.current) {
          gsap.fromTo(demoRef.current,
            { y: 80, opacity: 0, scale: 0.95 },
            { y: 0, opacity: 1, scale: 1, duration: 1, ease: 'power2.out',
              scrollTrigger: { trigger: demoRef.current, start: 'top 85%', toggleActions: 'play none none none' } }
          );
        }

        // ── Client Portal Cards ──
        if (portalRef.current) {
          gsap.fromTo(portalRef.current.children,
            { y: 60, opacity: 0, scale: 0.9 },
            { y: 0, opacity: 1, scale: 1, stagger: 0.12, duration: 0.7, ease: 'back.out(1.2)',
              scrollTrigger: { trigger: portalRef.current, start: 'top 85%', toggleActions: 'play none none none' } }
          );
        }

        // ── Pricing Cards ──
        if (pricingRef.current) {
          gsap.fromTo(pricingRef.current.children,
            { y: 80, opacity: 0, scale: 0.85 },
            { y: 0, opacity: 1, scale: 1, stagger: 0.15, duration: 0.8, ease: 'elastic.out(1, 0.5)',
              scrollTrigger: { trigger: pricingRef.current, start: 'top 85%', toggleActions: 'play none none none' } }
          );
        }

        // ── CTA Section ──
        if (ctaRef.current) {
          gsap.fromTo(ctaRef.current,
            { scale: 0.9, opacity: 0 },
            { scale: 1, opacity: 1, duration: 0.8,
              scrollTrigger: { trigger: ctaRef.current, start: 'top 90%', toggleActions: 'play none none none' } }
          );
        }

        // ── Carriers ──
        if (carriersRef.current) {
          gsap.fromTo(carriersRef.current.querySelectorAll('.carrier-chip'),
            { y: 20, opacity: 0 },
            { y: 0, opacity: 1, stagger: 0.04, duration: 0.4, ease: 'power2.out',
              scrollTrigger: { trigger: carriersRef.current, start: 'top 85%', toggleActions: 'play none none none' } }
          );
        }

        ScrollTrigger.refresh();
      }, mainRef);

      return () => ctx.revert();
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  const handleSelectPlan = (planId) => {
    if (planId === 'enterprise') { setShowContact(true); }
    else { setSelectedPlan(planId); setShowSignup(true); }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const regRes = await axios.post(`${API_URL}/api/companies/register`, { ...signupData, plan: selectedPlan });
      const companyId = regRes.data.company_id;
      const subRes = await axios.post(`${API_URL}/api/companies/subscribe`, { company_id: companyId, plan: selectedPlan, origin_url: window.location.origin, payment_method: paymentMethod });
      window.location.href = subRes.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed. Please try again.');
    } finally { setLoading(false); }
  };

  const handleContact = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/contact`, contactData);
      toast.success('Message sent! We\'ll get back to you within 24 hours.');
      setShowContact(false);
      setContactData({ name: '', email: '', company: '', message: '' });
    } catch {
      toast.success('Message received! We\'ll contact you soon.');
      setShowContact(false);
    }
  };

  const handleHeroTrack = (e) => {
    e.preventDefault();
    const num = heroTrackingNumber.trim();
    if (!num) { toast.error('Please enter a tracking number'); return; }
    navigate(`/track-package?q=${encodeURIComponent(num)}`);
  };

  return (
    <div ref={mainRef} className="min-h-screen bg-[#0B1120] overflow-hidden relative" data-testid="landing-page">
      <SEOHead
        title="Courier CRM & Universal Package Tracking"
        description="Manage your courier business like a pro. Universal tracking for UPS, FedEx, USPS, DHL & 1200+ carriers. Client portals, invoicing, Stripe & PayPal payments."
        path="/"
        keywords="courier CRM, package tracking, shipping management, USPS tracking, UPS tracking, FedEx tracking, DHL tracking, courier software, logistics SaaS"
        jsonLd={{ '@context': 'https://schema.org', '@type': 'WebSite', name: 'Track My Package', url: 'https://trackmypackage.app', description: 'All-in-one courier management platform with universal package tracking, client portals, invoicing, and payments.', potentialAction: { '@type': 'SearchAction', target: 'https://trackmypackage.app/track-package?q={search_term_string}', 'query-input': 'required name=search_term_string' } }}
      />

      {/* ═══ FLYING PLANE (GSAP scroll-driven) — visible on landing, in front of everything ═══ */}
      <div className="fixed inset-0 pointer-events-none z-[100] overflow-hidden">
        <div ref={planeRef} className="absolute will-change-transform">
          <PlaneSVG />
        </div>
      </div>

      {/* ═══ Ambient particles ═══ */}
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        {[...Array(20)].map((_, i) => (
          <div key={i} className="absolute w-1 h-1 bg-white/10 rounded-full animate-pulse"
            style={{ left: `${5 + i * 4.7}%`, top: `${10 + (i * 17) % 80}%`, animationDelay: `${i * 0.3}s`, animationDuration: `${2 + (i % 3)}s` }}
          />
        ))}
      </div>

      {/* ═══ NAVIGATION ═══ */}
      <PublicNav activePage="/" />

      {/* ═══════════════════════════════ HERO ═══════════════════════════════ */}
      <section className="pt-32 pb-16 px-6 relative overflow-hidden min-h-[90vh] flex items-center">
        <div className="absolute inset-0 bg-gradient-to-b from-[#003166]/20 via-transparent to-transparent pointer-events-none" />
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden">
          <img src="/assets/trackmypackage-logo.png" alt="" className="w-[800px] max-w-none opacity-[0.03] select-none" style={{ filter: 'grayscale(100%) brightness(2)' }} aria-hidden="true" />
        </div>

        <div className="max-w-6xl mx-auto relative z-10 w-full">
          <div className="text-center max-w-3xl mx-auto">
            <div ref={heroBadgeRef} className="inline-flex items-center gap-2 bg-[#FF9900]/10 text-[#FF9900] text-sm font-medium px-4 py-1.5 rounded-full mb-6 border border-[#FF9900]/20">
              <Lightning size={14} weight="fill" />
              {pt('hero.badge')}
            </div>

            <div ref={heroTextRef}>
              <h1 className="text-5xl sm:text-6xl lg:text-8xl font-bold tracking-tight leading-[0.95]">
                <StaggerText text={pt('hero.line1')} as="span" className="text-white block" />
                <StaggerText text={pt('hero.line2')} as="span" className="block" wordClassName="text-transparent bg-clip-text bg-gradient-to-r from-[#FF9900] to-[#FFB060]" delay={0.3} />
                <StaggerText text={pt('hero.line3')} as="span" className="text-white block" delay={0.5} />
              </h1>
              <StaggerText text={pt('hero.subtitle')} as="p" className="text-base sm:text-lg text-gray-400 mt-6 max-w-xl mx-auto" delay={0.7} />
            </div>

            <div ref={heroCtaRef} className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-8">
              <Button onClick={() => window.open('https://sqawk.app/book/MrPositivity', '_blank')}
                className="bg-[#FF9900] hover:bg-[#FF9900]/90 text-white text-lg px-8 py-6 rounded-xl shadow-lg shadow-[#FF9900]/20 group" data-testid="hero-cta-btn">
                {pt('nav.scheduleCall')}
                <ArrowRight size={20} className="ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>

            <p className="text-sm font-bold text-[#FF9900] mt-4 uppercase tracking-wider">
              {pt('hero.stopSpreadsheets')} <a href="https://sqawk.app/book/MrPositivity" target="_blank" rel="noopener noreferrer" className="text-white font-normal normal-case tracking-normal underline hover:text-[#FF9900] transition-colors">{pt('hero.scheduleToday')}</a>
            </p>

            <div ref={heroTrackRef} className="mt-8 max-w-lg mx-auto">
              <p className="text-gray-500 text-xs uppercase tracking-widest mb-2 font-semibold">{pt('hero.trackLabel')}</p>
              <form onSubmit={handleHeroTrack} className="flex gap-2" data-testid="hero-track-form">
                <div className="relative flex-1">
                  <MagnifyingGlass size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500" />
                  <Input value={heroTrackingNumber} onChange={e => setHeroTrackingNumber(e.target.value.toUpperCase())}
                    placeholder={pt('hero.trackPlaceholder')}
                    className="pl-10 h-12 rounded-xl text-sm font-mono bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-[#FF9900] focus:ring-[#FF9900]/20"
                    data-testid="hero-tracking-input" />
                </div>
                <Button type="submit" className="h-12 px-6 rounded-xl bg-gradient-to-r from-[#0080FF] to-[#0060CC] hover:from-[#0070EE] hover:to-[#0050BB] text-white font-semibold text-sm shadow-lg shadow-blue-500/20" data-testid="hero-track-submit-btn">
                  Track <ArrowRight size={16} className="ml-1.5" />
                </Button>
              </form>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10">
          <div className="w-6 h-10 rounded-full border-2 border-white/20 flex justify-center pt-2">
            <div className="w-1.5 h-3 bg-[#FF9900] rounded-full animate-bounce" />
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════ FEATURES ═══════════════════════════════ */}
      <section id="features" className="py-24 px-6 bg-[#0D1526] relative z-10">
        <div className="max-w-6xl mx-auto">
          <div ref={featTitleRef} className="text-center mb-16">
            <StaggerText text={pt('feat.title')} as="h2" className="text-3xl sm:text-4xl font-bold tracking-tight text-white" trigger={featTitleRef} />
            <StaggerText text={pt('feat.subtitle')} as="p" className="text-gray-400 mt-3 max-w-lg mx-auto text-base" trigger={featTitleRef} delay={0.3} />
          </div>
          <div ref={featGridRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {tFEATURES.map((feat, i) => (
              <div key={i} className="bg-white/[0.03] border border-white/5 p-6 rounded-2xl hover:border-[#FF9900]/30 transition-all duration-300 hover:scale-[1.03] hover:-translate-y-1 cursor-default group">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4 mx-auto group-hover:rotate-12 group-hover:scale-110 transition-transform duration-300"
                  style={{ backgroundColor: `${feat.color}15` }}>
                  <feat.icon size={22} style={{ color: feat.color }} weight="duotone" />
                </div>
                <h3 className="font-bold text-lg text-white mb-1 text-center">{feat.title}</h3>
                <p className="text-sm text-gray-400 text-center">{feat.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════ HOW IT WORKS ═══════════════════════════════ */}
      <section className="py-24 px-6 bg-[#060B15] relative z-10 overflow-hidden" id="how-it-works">
        <div className="max-w-5xl mx-auto relative z-10">
          <div ref={howTitleRef} className="text-center mb-14">
            <StaggerText text={pt('how.title')} as="h2" className="text-3xl sm:text-4xl font-bold tracking-tight text-white" trigger={howTitleRef} />
            <StaggerText text={pt('how.subtitle')} as="p" className="text-gray-400 mt-3" trigger={howTitleRef} delay={0.2} />
          </div>

          {/* Animated connector line */}
          <div className="hidden md:block relative mb-8 h-[2px]">
            <div className="absolute inset-0" style={{ background: 'linear-gradient(90deg, transparent 5%, #FF990040 25%, #0080FF40 50%, #FF990040 75%, transparent 95%)' }} />
          </div>

          <div ref={howStepsRef} className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { num: '01', icon: Users, title: pt('how.step1Title'), desc: pt('how.step1Desc'), color: '#0080FF' },
              { num: '02', icon: Package, title: pt('how.step2Title'), desc: pt('how.step2Desc'), color: '#FF9900' },
              { num: '03', icon: Airplane, title: pt('how.step3Title'), desc: pt('how.step3Desc'), color: '#0080FF' },
              { num: '04', icon: CreditCard, title: pt('how.step4Title'), desc: pt('how.step4Desc'), color: '#FF9900' },
            ].map((step, i) => (
              <div key={i} className="relative group">
                <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-6 h-full hover:border-[#FF9900]/40 transition-all duration-300 hover:-translate-y-2">
                  <span className="text-5xl font-bold block" style={{ fontFamily: 'Bebas Neue', color: `${step.color}30` }}>{step.num}</span>
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mt-2 mb-3 group-hover:rotate-[360deg] transition-transform duration-500"
                    style={{ backgroundColor: `${step.color}15` }}>
                    <step.icon size={24} style={{ color: step.color }} weight="duotone" />
                  </div>
                  <h3 className="font-bold text-white text-lg mb-1.5">{step.title}</h3>
                  <p className="text-sm text-gray-400">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <Button onClick={() => window.open('https://sqawk.app/book/MrPositivity', '_blank')}
              className="bg-[#FF9900] hover:bg-[#FF9900]/90 text-white text-base px-8 py-5 rounded-xl shadow-lg shadow-[#FF9900]/20 group" data-testid="explainer-cta-btn">
              {pt('nav.scheduleCall')}
              <ArrowRight size={18} className="ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════ ANIMATED DEMO ═══════════════════════════════ */}
      <section className="py-24 px-6 bg-[#0B1120] relative z-10 overflow-hidden" id="demo">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <StaggerText text={pt('demo.title')} as="h2" className="text-3xl sm:text-4xl font-bold tracking-tight text-white" trigger={demoRef} />
            <StaggerText text={pt('demo.subtitle')} as="p" className="text-gray-400 mt-3" trigger={demoRef} delay={0.2} />
          </div>
          <div ref={demoRef}>
            <AnimatedDemo />
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════ CLIENT PORTAL ═══════════════════════════════ */}
      <section className="py-24 px-6 bg-[#060B15] relative z-10 overflow-hidden" id="client-portal">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 bg-[#0080FF]/10 text-[#0080FF] text-sm font-medium px-4 py-1.5 rounded-full mb-5 border border-[#0080FF]/20">
              <ShieldCheck size={14} weight="fill" /> {pt('portal.badge')}
            </div>
            <StaggerText text={pt('portal.title')} as="h2" className="text-3xl sm:text-4xl font-bold tracking-tight text-white" trigger={portalRef} />
            <StaggerText text={pt('portal.subtitle')} as="p" className="text-gray-400 mt-3 max-w-xl mx-auto text-base" trigger={portalRef} delay={0.2} />
          </div>

          <div ref={portalRef} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {[
              { icon: Package, title: pt('portal.submit'), desc: pt('portal.submitDesc'), color: '#FF9900' },
              { icon: MapPin, title: pt('portal.status'), desc: pt('portal.statusDesc'), color: '#0080FF' },
              { icon: CreditCard, title: pt('portal.invoices'), desc: pt('portal.invoicesDesc'), color: '#10B981' },
              { icon: BellRinging, title: pt('portal.push'), desc: pt('portal.pushDesc'), color: '#F43F5E' },
            ].map((card, i) => (
              <div key={i} className="relative group cursor-default" data-testid={`fact-card-${i}`}>
                <div className={`absolute inset-0 bg-gradient-to-b from-[${card.color}]/10 to-transparent rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                <div className="relative bg-white/[0.04] border border-white/10 rounded-2xl p-7 h-full hover:border-white/20 transition-all duration-300 hover:-translate-y-2">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5 group-hover:rotate-12 group-hover:scale-110 transition-transform duration-300"
                    style={{ backgroundColor: `${card.color}15` }}>
                    <card.icon size={28} style={{ color: card.color }} weight="duotone" />
                  </div>
                  <h3 className="font-bold text-lg text-white mb-2">{card.title}</h3>
                  <p className="text-sm text-gray-400 leading-relaxed">{card.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-gray-500 text-sm mb-4">{pt('portal.saveHome')}</p>
            <Button onClick={() => window.open('https://sqawk.app/book/MrPositivity', '_blank')}
              className="bg-[#0080FF] hover:bg-[#0070EE] text-white text-base px-8 py-5 rounded-xl shadow-lg shadow-blue-500/20" data-testid="fact-cards-cta-btn">
              {pt('portal.seeAction')} <ArrowRight size={18} className="ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════ PRICING ═══════════════════════════════ */}
      <section id="pricing" className="py-24 px-6 relative z-10">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <StaggerText text={pt('price.title')} as="h2" className="text-3xl sm:text-4xl font-bold tracking-tight text-white" trigger={pricingRef} />
            <StaggerText text={pt('price.subtitle')} as="p" className="text-gray-400 mt-3" trigger={pricingRef} delay={0.2} />
          </div>
          <div ref={pricingRef} className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {tPLANS.map((plan, i) => (
              <div key={plan.id}
                className={`relative rounded-2xl p-6 transition-transform duration-300 hover:-translate-y-2 ${
                  plan.popular
                    ? 'bg-gradient-to-b from-[#FF9900] to-[#E06A00] text-white shadow-xl scale-105 border-2 border-[#FF9900]'
                    : 'bg-white/[0.03] border border-white/10'
                }`} data-testid={`plan-card-${plan.id}`}>
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#003166] text-white text-xs font-bold px-4 py-1 rounded-full flex items-center gap-1">
                    <Star size={12} weight="fill" /> MOST POPULAR
                  </div>
                )}
                <h3 className="text-2xl font-bold text-white">{plan.name}</h3>
                <p className={`text-sm mt-1 ${plan.popular ? 'text-white/70' : 'text-gray-400'}`}>{plan.description}</p>
                <div className="my-6">
                  {plan.price ? (
                    <><span className="text-4xl font-bold text-white">${plan.price}</span><span className={`text-sm ${plan.popular ? 'text-white/70' : 'text-gray-400'}`}>{pt('price.month')}</span></>
                  ) : (
                    <span className="text-2xl font-bold text-white">{pt('price.custom')}</span>
                  )}
                </div>
                <ul className="space-y-2.5 mb-8">
                  {plan.features.map((feat, fi) => (
                    <li key={fi} className="flex items-start gap-2 text-sm">
                      <Check size={16} weight="bold" className={`mt-0.5 flex-shrink-0 ${plan.popular ? 'text-white' : 'text-[#FF9900]'}`} />
                      <span className={plan.popular ? 'text-white/90' : 'text-gray-300'}>{feat}</span>
                    </li>
                  ))}
                </ul>
                <Button onClick={() => handleSelectPlan(plan.id)}
                  className={`w-full rounded-xl py-5 font-medium group ${plan.popular ? 'bg-white text-[#FF9900] hover:bg-white/90' : 'bg-[#FF9900] hover:bg-[#FF9900]/90 text-white'}`}
                  data-testid={`plan-select-${plan.id}`}>
                  {plan.cta} <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </div>
            ))}
          </div>

          {/* Support Add-on */}
          <div className="mt-10 max-w-3xl mx-auto">
            <div className="relative bg-white/[0.03] border border-white/10 rounded-2xl p-6 flex flex-col sm:flex-row items-center gap-6 hover:border-[#0077FF]/30 transition-colors" data-testid="support-addon-card">
              <div className="w-14 h-14 rounded-2xl bg-[#0077FF]/10 flex items-center justify-center flex-shrink-0">
                <Phone size={28} className="text-[#0077FF]" weight="duotone" />
              </div>
              <div className="flex-1 text-center sm:text-left">
                <h3 className="text-xl font-bold text-white">{pt('price.supportTitle')}</h3>
                <p className="text-sm text-gray-400 mt-1">{pt('price.supportDesc')}</p>
              </div>
              <div className="text-center flex-shrink-0">
                <div className="mb-2"><span className="text-3xl font-bold text-white">$99</span><span className="text-sm text-gray-400">{pt('price.month')}</span></div>
                <Button onClick={() => window.open('https://sqawk.app/book/MrPositivity', '_blank')} className="bg-[#0077FF] hover:bg-[#0066DD] text-white rounded-xl px-6 text-sm" data-testid="support-addon-btn">
                  Add Support <ArrowRight size={14} className="ml-1.5" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════ CTA ═══════════════════════════════ */}
      <section ref={ctaRef} className="py-16 px-6 bg-gradient-to-r from-[#003166] to-[#0077FF] relative z-10 overflow-hidden">
        <div className="max-w-3xl mx-auto text-center text-white relative z-10">
          <StaggerText text={pt('cta.growTitle')} as="h2" className="text-3xl sm:text-4xl font-bold tracking-tight" trigger={ctaRef} />
          <StaggerText text={pt('cta.growSubtitle')} as="p" className="text-white/60 mt-3 text-base sm:text-lg" trigger={ctaRef} delay={0.2} />
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-8">
            <Button onClick={() => window.open('https://sqawk.app/book/MrPositivity', '_blank')}
              className="bg-[#FF9900] hover:bg-[#FF9900]/90 text-white text-lg px-8 py-6 rounded-xl font-medium group">
              {pt('cta.getStartedNow')} <ArrowRight size={20} className="ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button onClick={() => setShowContact(true)} variant="outline" className="text-lg px-8 py-6 rounded-xl border-white/20 text-white hover:bg-white/10">
              {pt('cta.contactSales')}
            </Button>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════ CARRIERS ═══════════════════════════════ */}
      <section ref={carriersRef} className="py-16 px-6 bg-[#060B15] relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-white mb-3" style={{ fontFamily: 'Bebas Neue', letterSpacing: '0.05em' }}>{pt('carriers.title')}</h2>
          <p className="text-[#FF9900] text-base font-semibold mb-8 max-w-xl mx-auto">{pt('carriers.subtitle')}</p>
          <div className="flex flex-wrap justify-center gap-4 text-xs text-gray-500 mb-8">
            {['UPS Tracking', 'FedEx Tracking', 'USPS Tracking', 'DHL Tracking', 'Amazon Tracking', 'TNT Tracking', 'Aramex Tracking', 'Canada Post', 'Royal Mail', 'Australia Post', 'La Poste', 'Deutsche Post'].map((c) => (
              <span key={c} className="carrier-chip px-3 py-1.5 bg-white/5 border border-white/10 rounded-full">{c}</span>
            ))}
          </div>
          <Button onClick={() => navigate('/track-package')}
            className="bg-[#FF9900] hover:bg-[#FF9900]/90 text-white text-base px-8 py-5 rounded-xl shadow-lg shadow-[#FF9900]/20" data-testid="carriers-track-btn">
            <MagnifyingGlass size={20} className="mr-2" /> {pt('carriers.trackBtn')}
          </Button>
        </div>
      </section>

      {/* ═══ FOOTER ═══ */}
      <PublicFooter />

      {/* ═══════════════════════════════ SIGNUP MODAL ═══════════════════════════════ */}
      <AnimatePresence>
        {showSignup && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setShowSignup(false)}>
            <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-[#111827] border border-white/10 rounded-2xl shadow-2xl max-w-md w-full p-8" onClick={e => e.stopPropagation()} data-testid="signup-modal">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-2xl font-bold text-white">{pt('signup.title')}</h3>
                  <p className="text-sm text-gray-400 mt-1">{tPLANS.find(p => p.id === selectedPlan)?.name} Plan - ${tPLANS.find(p => p.id === selectedPlan)?.price}{pt('price.month')}</p>
                </div>
                <button onClick={() => setShowSignup(false)} className="p-2 hover:bg-white/5 rounded-lg"><X size={20} className="text-gray-400" /></button>
              </div>
              <form onSubmit={handleSignup} className="space-y-4">
                <div>
                  <label className="text-xs font-medium text-gray-400 uppercase tracking-wide">{pt('signup.companyName')}</label>
                  <Input value={signupData.company_name} onChange={e => setSignupData({...signupData, company_name: e.target.value})} placeholder="Your Courier Company" className="rounded-xl h-11 mt-1 bg-white/5 border-white/10 text-white placeholder:text-gray-500" required data-testid="signup-company-name" />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-400 uppercase tracking-wide">{pt('signup.email')}</label>
                  <Input type="email" value={signupData.email} onChange={e => setSignupData({...signupData, email: e.target.value})} placeholder="admin@yourcompany.com" className="rounded-xl h-11 mt-1 bg-white/5 border-white/10 text-white placeholder:text-gray-500" required data-testid="signup-email" />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-400 uppercase tracking-wide">{pt('signup.password')}</label>
                  <Input type="password" value={signupData.password} onChange={e => setSignupData({...signupData, password: e.target.value})} placeholder="Min. 6 characters" className="rounded-xl h-11 mt-1 bg-white/5 border-white/10 text-white placeholder:text-gray-500" required minLength={6} data-testid="signup-password" />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-400 uppercase tracking-wide">{pt('signup.phone')}</label>
                  <Input type="tel" value={signupData.phone} onChange={e => setSignupData({...signupData, phone: e.target.value})} placeholder="+1 555 123 4567" className="rounded-xl h-11 mt-1 bg-white/5 border-white/10 text-white placeholder:text-gray-500" data-testid="signup-phone" />
                </div>
                <div className="flex gap-2 p-1 bg-white/5 rounded-xl">
                  {['starter', 'pro'].map(pid => (
                    <button key={pid} type="button" onClick={() => setSelectedPlan(pid)}
                      className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${selectedPlan === pid ? 'bg-[#FF9900] text-white shadow' : 'text-gray-400'}`}
                      data-testid={`signup-plan-${pid}`}>
                      {tPLANS.find(p => p.id === pid)?.name} - ${tPLANS.find(p => p.id === pid)?.price}/mo
                    </button>
                  ))}
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1.5 block">{pt('signup.paymentMethod')}</label>
                  <div className="flex gap-2 p-1 bg-white/5 rounded-xl">
                    <button type="button" onClick={() => setPaymentMethod('stripe')}
                      className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${paymentMethod === 'stripe' ? 'bg-[#635BFF] text-white shadow' : 'text-gray-400 hover:text-gray-300'}`}
                      data-testid="payment-method-stripe">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-7.076-2.19l-.917 5.612C5.038 23.202 8.003 24 11.19 24c2.586 0 4.735-.681 6.233-1.848 1.65-1.29 2.507-3.21 2.507-5.674C19.93 12.408 17.5 10.458 13.976 9.15z"/></svg>
                      Stripe
                    </button>
                    <button type="button" onClick={() => setPaymentMethod('paypal')}
                      className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${paymentMethod === 'paypal' ? 'bg-[#0070BA] text-white shadow' : 'text-gray-400 hover:text-gray-300'}`}
                      data-testid="payment-method-paypal">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 2.56A.77.77 0 0 1 5.704 2h6.865c3.116 0 5.31 2.272 4.838 5.15-.566 3.442-3.477 5.478-6.78 5.478H8.476l-1.4 8.709zm13.678-15.2c.566-3.442-1.722-5.78-4.838-5.78H9.05a.77.77 0 0 0-.76.56L5.183 19.45a.641.641 0 0 0 .633.74h3.39l.852-5.302a.77.77 0 0 1 .76-.56h2.15c3.303 0 6.214-2.036 6.78-5.478l.006-.034z"/></svg>
                      PayPal
                    </button>
                  </div>
                </div>
                <Button type="submit" disabled={loading} className="w-full bg-[#FF9900] hover:bg-[#FF9900]/90 text-white rounded-xl py-5 text-base font-medium" data-testid="signup-submit-btn">
                  {loading ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <>{pt('signup.continueTo')} {paymentMethod === 'paypal' ? 'PayPal' : 'Stripe'} <ArrowRight size={18} className="ml-2" /></>}
                </Button>
                <p className="text-xs text-gray-500 text-center">{pt('signup.bySigningUp')} {paymentMethod === 'paypal' ? 'PayPal' : 'Stripe'} {pt('signup.forPayment')}</p>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ═══════════════════════════════ CONTACT MODAL ═══════════════════════════════ */}
      <AnimatePresence>
        {showContact && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setShowContact(false)}>
            <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-[#111827] border border-white/10 rounded-2xl shadow-2xl max-w-md w-full p-8" onClick={e => e.stopPropagation()} data-testid="contact-modal">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-2xl font-bold text-white">{pt('contact.title')}</h3>
                  <p className="text-sm text-gray-400 mt-1">{pt('contact.subtitle')}</p>
                </div>
                <button onClick={() => setShowContact(false)} className="p-2 hover:bg-white/5 rounded-lg"><X size={20} className="text-gray-400" /></button>
              </div>
              <form onSubmit={handleContact} className="space-y-4">
                <div>
                  <label className="text-xs font-medium text-gray-400 uppercase tracking-wide">{pt('contact.yourName')}</label>
                  <Input value={contactData.name} onChange={e => setContactData({...contactData, name: e.target.value})} placeholder="John Doe" className="rounded-xl h-11 mt-1 bg-white/5 border-white/10 text-white placeholder:text-gray-500" required data-testid="contact-name" />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-400 uppercase tracking-wide">{pt('signup.email')}</label>
                  <Input type="email" value={contactData.email} onChange={e => setContactData({...contactData, email: e.target.value})} placeholder="you@company.com" className="rounded-xl h-11 mt-1 bg-white/5 border-white/10 text-white placeholder:text-gray-500" required data-testid="contact-email" />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-400 uppercase tracking-wide">{pt('contact.companyName')}</label>
                  <Input value={contactData.company} onChange={e => setContactData({...contactData, company: e.target.value})} placeholder="Your Logistics Co." className="rounded-xl h-11 mt-1 bg-white/5 border-white/10 text-white placeholder:text-gray-500" required data-testid="contact-company" />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-400 uppercase tracking-wide">{pt('contact.message')}</label>
                  <textarea value={contactData.message} onChange={e => setContactData({...contactData, message: e.target.value})} placeholder={pt('contact.messagePlaceholder')} className="w-full rounded-xl mt-1 p-3 bg-white/5 border border-white/10 text-white placeholder:text-gray-500 text-sm min-h-[100px] focus:outline-none focus:ring-1 focus:ring-[#FF9900] focus:border-[#FF9900]" required data-testid="contact-message" />
                </div>
                <Button type="submit" className="w-full bg-[#003166] hover:bg-[#003166]/90 text-white rounded-xl py-5 text-base font-medium" data-testid="contact-submit-btn">
                  {pt('contact.sendMessage')} <ArrowRight size={18} className="ml-2" />
                </Button>
                <div className="text-center">
                  <p className="text-gray-500 text-xs mb-2">{pt('contact.reachDirectly')}</p>
                  <a href="https://wa.me/50586156364?text=Hi!%20I%27m%20interested%20in%20the%20Enterprise%20plan."
                    target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-[#25D366] text-sm hover:underline" data-testid="contact-whatsapp-link">
                    <WhatsappLogo size={16} weight="fill" /> {pt('contact.chatWhatsApp')}
                  </a>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <ChatWidget />
    </div>
  );
}
